/**
 * KMMO kit additions owned by the lobby group: the app-aware screen shell every meta screen can
 * use (top bar with back / title / (i), the 3 currencies kept live from the store, ⚙ settings
 * menu, ⇥ to the lobby), the ⚙ settings sheet (graphics, bot difficulty, export / import /
 * reset), the "How this works" info sheet, and a stage helper that falls back to a big class
 * crest when the live 3D hero must not render (in-run overlay, placeholder renderer).
 *
 *   const shell = metaShell(ctx, { title: 'Hero', info: () => 'Your gear and bag.' });
 *   const stage = heroStageOrCrest(ctx, shell.frame.backdrop, { heroes: [main] });
 *   return { el: shell.el, dispose() { stage.dispose(); shell.dispose(); } };
 */
import type { BotSkillPreset } from '@keystone/sim';
import type { MetaScreenContext } from '../context';
import { el } from '../ui/dom';
import { BOT_PRESET_NAMES } from '../../hud/screens';
import { type CurrencySpec, type ItemSlotHandle, type ItemSlotOptions, type ScreenFrame, classCrest, itemSlot, divider, ghostButton, kmModal, kmToast, primaryButton, screenFrame, secondaryButton, stepper } from './components';
import { type KmIcon, RESOURCE_ICON, goldCoinSvg, iconUrl, kmIcon } from './icons';
import type { KmRarity } from './helpers';
import { type HeroStage, type HeroStageOptions, mountHeroStage } from './heroStage';
import './extra-lobby.css';

/** The only currencies shown in any top bar (art direction): gold, Fangsteel, Altar Sigils. */
export function topCurrencies(ctx: Pick<MetaScreenContext, 'store'>, labels = false): CurrencySpec[] {
  const p = ctx.store.profile;
  return [
    { id: 'gold', icon: 'gold', value: p.currencies.gold, title: 'Gold', label: labels ? 'gold' : undefined },
    { id: 'fangsteel', icon: iconUrl(RESOURCE_ICON['fangsteel']!, true), value: p.wallet.fangsteel, title: 'Fangsteel', label: labels ? 'Fangsteel' : undefined },
    { id: 'sigils', icon: iconUrl(RESOURCE_ICON['sigils']!, true), value: p.wallet.sigils, title: 'Altar Sigils', label: labels ? (p.wallet.sigils === 1 ? 'Altar Sigil' : 'Altar Sigils') : undefined },
  ];
}

export interface MetaShellOptions {
  title: string;
  /** "How this works" sheet behind the (i) button (text paragraphs or an element). */
  info?: () => HTMLElement | string | readonly string[];
  /** Back arrow (default true; the lobby passes false). */
  back?: boolean;
  /** KEYSTONE wordmark instead of the title (lobby). */
  brand?: boolean;
  center?: string | HTMLElement;
  /** ⇥ to the lobby (default: on unless `brand`). */
  exit?: boolean;
  /** Currency labels ("12,480 gold"; lobby). */
  currencyLabels?: boolean;
  className?: string;
  /** Override the back action (default `ctx.router.back()`). */
  onBack?: () => void;
}

export interface MetaShell {
  el: HTMLElement;
  frame: ScreenFrame;
  dispose(): void;
}

/** Full-screen KMMO frame wired to the app: live currencies, ⚙ settings, ⇥ lobby, (i) sheet. */
export function metaShell(ctx: MetaScreenContext, o: MetaShellOptions): MetaShell {
  const utilities: { icon: KmIcon; title: string; onClick: () => void }[] = [{ icon: 'settings', title: 'Settings', onClick: () => openSettingsSheet(ctx) }];
  if (o.exit ?? !o.brand) utilities.push({ icon: 'exit', title: 'Back to the world', onClick: () => ctx.router.reset() });
  const frame = screenFrame({
    title: o.title,
    brand: o.brand,
    center: o.center,
    onBack: o.back === false ? undefined : (o.onBack ?? (() => ctx.router.back())),
    onInfo: o.info ? () => openInfoSheet(o.title, o.info!()) : undefined,
    currencies: topCurrencies(ctx, o.currencyLabels),
    utilities,
    className: `${o.className ?? ''}${ctx.router.isOverlay ? ' km-as-overlay' : ''}`.trim(),
  });
  const unsub = ctx.store.subscribe(() => {
    for (const c of topCurrencies(ctx)) frame.currencies?.set(c.id, c.value);
  });
  return {
    el: frame.el,
    frame,
    dispose() {
      unsub();
    },
  };
}

/** "How this works" sheet: a few short paragraphs (all the advanced explanations live here). */
export function openInfoSheet(title: string, content: HTMLElement | string | readonly string[]): void {
  const m = kmModal(title, { wide: typeof content !== 'string' && !Array.isArray(content) });
  m.el.classList.add('km-info-sheet');
  if (typeof content === 'string') el('p', 'km-modal-text', m.body, content);
  else if (Array.isArray(content)) for (const t of content as readonly string[]) el('p', 'km-modal-text', m.body, t);
  else m.body.appendChild(content as HTMLElement);
  primaryButton('OK', { onClick: () => m.close() }, m.footer);
}

const cap = (s: string): string => s[0]!.toUpperCase() + s.slice(1);

/** ⚙ menu: Graphics, Bot difficulty, Export / Import / Reset profile. */
export function openSettingsSheet(ctx: MetaScreenContext): void {
  const m = kmModal('Settings');
  m.el.classList.add('km-settings');
  const row = (label: string): HTMLElement => {
    const r = el('div', 'km-set-row', m.body);
    el('span', 'km-set-label', r, label);
    return el('div', 'km-set-ctl', r);
  };
  if (ctx.openGraphics) secondaryButton('Open', { size: 'sm', onClick: () => (m.close(), ctx.openGraphics!()) }, row('Graphics'));
  // +tutorial: replay the guided first run (tutorial/app.ts).
  if (ctx.openTutorial) secondaryButton('Replay', { size: 'sm', onClick: () => (m.close(), ctx.openTutorial!()) }, row('Tutorial'));
  const names = BOT_PRESET_NAMES;
  const cur = Math.max(0, names.indexOf(ctx.store.profile.settings.defaultBotPreset));
  stepper({
    value: cur,
    min: 0,
    max: names.length - 1,
    format: (i) => cap(names[i] ?? ''),
    onChange: (i) => {
      void ctx.store.act('botPreset', { preset: names[i] as BotSkillPreset });
    },
  }, row('Bot difficulty'));
  divider(m.body);
  const prof = row('Profile');
  secondaryButton('Export', { size: 'sm', onClick: () => exportProfile(ctx) }, prof);
  secondaryButton('Import', { size: 'sm', onClick: () => (m.close(), importProfile(ctx)) }, prof);
  ghostButton('Reset', {
    size: 'sm',
    className: 'km-danger',
    onClick: () => {
      m.close();
      const c = kmModal('Reset profile?');
      el('p', 'km-modal-text', c.body, 'This deletes your heroes, gear, resources and rating. Export first to keep a backup.');
      secondaryButton('Cancel', { onClick: () => c.close() }, c.footer);
      primaryButton('Reset', {
        onClick: () => {
          c.close();
          ctx.store.reset();
          kmToast('Started a new profile', 'success');
          ctx.router.reset();
        },
      }, c.footer);
    },
  }, prof);
  el('div', 'km-set-foot', m.body, `${ctx.store.profile.runs.length} run${ctx.store.profile.runs.length === 1 ? '' : 's'} played`);
}

async function copyText(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    /* fall through */
  }
  return false;
}

function exportProfile(ctx: MetaScreenContext): void {
  const json = ctx.store.exportJson();
  void copyText(json).then((ok) => {
    if (ok) {
      kmToast('Profile copied to the clipboard', 'success');
      return;
    }
    const m = kmModal('Export profile', { wide: true });
    el('p', 'km-modal-text', m.body, 'Copy this text and keep it somewhere safe.');
    const ta = el('textarea', 'km-textarea', m.body);
    ta.readOnly = true;
    ta.value = json;
    primaryButton('Done', { onClick: () => m.close() }, m.footer);
    ta.focus();
    ta.select();
  });
}

function importProfile(ctx: MetaScreenContext): void {
  const m = kmModal('Import profile', { wide: true });
  el('p', 'km-modal-text', m.body, 'Paste an exported profile. It replaces your current one.');
  const ta = el('textarea', 'km-textarea', m.body);
  ta.placeholder = '{"version":3,…}';
  const err = el('div', 'km-form-error', m.body);
  secondaryButton('Cancel', { onClick: () => m.close() }, m.footer);
  primaryButton('Import', {
    onClick: () => {
      if (!ta.value.trim()) {
        err.textContent = 'Paste a profile first.';
        return;
      }
      const r = ctx.store.importJson(ta.value);
      if (!r.ok) {
        err.textContent = r.error;
        return;
      }
      m.close();
      kmToast(r.migratedFrom !== null ? `Profile imported (upgraded from v${r.migratedFrom})` : 'Profile imported', 'success');
      ctx.router.reset();
    },
  }, m.footer);
  setTimeout(() => ta.focus(), 0);
}

// ---------------------------------------------------------------------------
// Stage with a crest fallback
// ---------------------------------------------------------------------------

export interface StageHandle {
  /** True when the live 3D hero is shown (false = crest fallback). */
  live: boolean;
  setHeroes(heroes: readonly string[]): void;
  dispose(): void;
}

/** True when a screen may render the live 3D hero (not over a run, not the placeholder renderer). */
export function stageAllowed(ctx: Pick<MetaScreenContext, 'router'> & { stageAllowed?: () => boolean }): boolean {
  if (ctx.router.isOverlay) return false;
  try {
    return ctx.stageAllowed?.() ?? true;
  } catch {
    return false;
  }
}

/**
 * The live VRM hero(es) on the studio stage, or (in-run overlay / placeholder renderer / WebGL
 * failure) a large class crest centred where the hero would stand.
 */
export function heroStageOrCrest(ctx: MetaScreenContext, host: HTMLElement, o: HeroStageOptions): StageHandle {
  let stage: HeroStage | null = null;
  let crestBox: HTMLElement | null = null;
  const crest = (heroes: readonly string[]): void => {
    crestBox?.remove();
    crestBox = el('div', 'km-stage-fallback', host);
    crestBox.style.left = `${(o.centerX ?? 0.5) * 100}%`;
    for (const h of heroes) classCrest(h, { size: 'lg' }, crestBox);
  };
  if (stageAllowed(ctx)) {
    try {
      stage = mountHeroStage(host, o);
    } catch (err) {
      console.warn('[meta] hero stage failed; showing the crest', err);
      stage = null;
    }
  }
  if (!stage) crest(o.heroes);
  return {
    live: !!stage,
    setHeroes(heroes) {
      if (stage) void stage.setHeroes(heroes);
      else crest(heroes);
    },
    dispose() {
      stage?.dispose();
      crestBox?.remove();
    },
  };
}

// ---------------------------------------------------------------------------
// Resource slot (painted icon on a rarity tile + count)
// ---------------------------------------------------------------------------

/** Tile colour per resource (rewards / materials rows): Fangsteel blue, Jade green, Venom purple… */
export const RESOURCE_RARITY: Readonly<Record<string, KmRarity>> = {
  fangsteel: 'rare',
  'scale.ashen': 'common',
  'scale.jade': 'uncommon',
  'scale.gilded': 'legendary',
  'scale.eternal': 'epic',
  venom: 'epic',
  moltskin: 'common',
  eyeFragments: 'legendary',
  sigils: 'legendary',
  blessed: 'rare',
  vaultToken: 'epic',
  gold: 'legendary',
  xp: 'rare',
};

/** Icon for a resource key ('gold' = the drawn coin, 'xp' = a star, otherwise the painted icon). */
export function resourceIcon(key: string): Element | string {
  if (key === 'gold') {
    const c = goldCoinSvg();
    c.classList.add('km-res-coin');
    return c;
  }
  if (key === 'xp') return kmIcon('star', 'km-res-xp');
  const stem = RESOURCE_ICON[key];
  return stem ? iconUrl(stem) : kmIcon('diamond');
}

/** Square resource slot: painted icon on its rarity tile, count badge (shown from 1). */
export function resourceSlot(key: string, n: number, o: ItemSlotOptions = {}, parent?: HTMLElement): ItemSlotHandle {
  const h = itemSlot({ rarity: RESOURCE_RARITY[key] ?? 'common', icon: resourceIcon(key), ...o }, parent);
  if (n > 0 && !(o.count !== undefined && o.count > 1)) {
    const b = el('span', 'km-badge-count km-num', h.slot, n.toLocaleString('en-US'));
    b.classList.add('km-res-count');
  }
  return h;
}
